import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../auth/AuthContext';
import { completeLogin } from '../auth/completeLogin';
import api from '../api/axios';
import { COLORS as C } from '../constants/ui';

/**
 * Where Microsoft sign-in lands.
 *
 * The URL carries a single-use ticket, not a token — the JWT arrives in the
 * POST response body so it never enters browser history or a Referer header.
 */
export default function SsoCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, syncSessionFromServer } = useContext(AuthContext);
  const [error, setError] = useState('');
  // Tickets are single-use, so a double-invoke (StrictMode) must not spend it twice.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const ticket = params.get('ticket');
    if (!ticket) {
      navigate('/?sso_error=MISSING_TICKET', { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post('/auth/microsoft/exchange', { ticket });
        const { needsOnboarding } = await completeLogin(data, {
          login,
          syncSessionFromServer,
          navigate: (to) => navigate(to, { replace: true }),
        });
        toast.success(needsOnboarding ? 'Welcome! Just a couple of details.' : 'Welcome back!');
      } catch (err) {
        const message =
          err.response?.data?.message || 'Sign-in could not be completed. Please try again.';
        setError(message);
        setTimeout(() => navigate('/?sso_error=EXCHANGE_FAILED', { replace: true }), 1800);
      }
    })();
  }, [params, navigate, login, syncSessionFromServer]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 14,
        background: C.sunken,
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      {error ? (
        <>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: C.danger }}>
            Sign-in failed
          </div>
          <div style={{ color: C.inkSoft, fontSize: '0.9rem', maxWidth: 420 }}>{error}</div>
        </>
      ) : (
        <>
          <div
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: `3px solid ${C.line}`,
              borderTopColor: C.accentInk,
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ color: C.inkSoft, fontSize: '0.9rem' }}>Signing you in…</div>
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </>
      )}
    </div>
  );
}
